import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AttachmentPreview from "../AttachmentPreview";
import { AttachmentPreviewEditor } from "../AttachmentPreviewEditor";
import { AttachmentPreviewInline } from "../AttachmentPreviewInline";
import { DateTimeDisplay } from "../DateTimeDisplay";
import {
  useAttachments,
  useUpdateAttachment,
} from "../../hooks/useAttachments";
import { formatSize } from "../../logic/format-size";
import type { AttachmentDto } from "../../api/types";

export interface AttachmentManageDialogProps {
  open: boolean;
  attachment: AttachmentDto | null;
  onClose: () => void;
}

export const AttachmentManageDialog = ({
  open,
  attachment,
  onClose,
}: AttachmentManageDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isWide = useMediaQuery(theme.breakpoints.up("md"));

  const [filename, setFilename] = useState(attachment?.filename ?? "");
  const [notes, setNotes] = useState(attachment?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateAttachment();
  // Both callers hand over a snapshot held in their own state, which goes stale
  // the moment the preview editor writes a new version — and the preview URL is
  // keyed on that version, so the new image would never appear. The live row
  // from the list (which the editor invalidates) drives the preview only: the
  // form fields keep comparing against the snapshot they were seeded from.
  const { data: allAttachments } = useAttachments();

  const current = attachment;
  if (!current) return null;
  const livePreviewSource =
    allAttachments?.find((a) => a.id === current.id) ?? current;

  const trimmedFilename = filename.trim();
  const trimmedNotes = notes.trim();
  const isDirty =
    trimmedFilename !== current.filename ||
    trimmedNotes !== (current.notes ?? "");
  const canSave = trimmedFilename.length > 0 && isDirty;

  const handleSave = () => {
    setError(null);
    updateMutation.mutate(
      {
        id: current.id,
        body: {
          filename: trimmedFilename,
          notes: trimmedNotes.length > 0 ? trimmedNotes : null,
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e) =>
          setError(e instanceof Error ? e.message : "Failed to save"),
      },
    );
  };

  const metadataBlock = (
    <Stack spacing={0.5}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        Type: {current.contentType} · {formatSize(current.size)}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        Uploaded <DateTimeDisplay date={current.createdAt} />
      </Typography>
    </Stack>
  );

  const formFields = (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        required
        label="Filename"
        value={filename}
        onChange={(e) => setFilename(e.target.value)}
        fullWidth
        slotProps={{ htmlInput: { maxLength: 255 } }}
      />
      <TextField
        label="Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        multiline
        rows={4}
        placeholder="Optional context — e.g. vendor, invoice date, category"
        slotProps={{ htmlInput: { maxLength: 2000 } }}
      />
      {metadataBlock}
      {/* The wide layout offers this on the preview itself; the narrow one has
          only a thumbnail, so it needs the action spelled out here. */}
      {!isWide && (
        <Box>
          <AttachmentPreviewEditor attachment={livePreviewSource} />
        </Box>
      )}
    </Stack>
  );

  const previewPane = (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <AttachmentPreviewInline attachment={livePreviewSource} />
    </Box>
  );

  const mobilePreview = (
    <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
      <AttachmentPreview attachment={livePreviewSource} />
    </Box>
  );

  const primaryButton = isDirty ? (
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={!canSave || updateMutation.isPending}
    >
      Save
    </Button>
  ) : (
    <Button variant="contained" onClick={onClose}>
      Close
    </Button>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth={isWide ? "lg" : "sm"}
        fullWidth
      >
        <DialogTitle>Manage attachment</DialogTitle>
        <DialogContent dividers>
          {isWide ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                alignItems: "stretch",
                minHeight: 500,
              }}
            >
              <Box sx={{ minHeight: 0 }}>{previewPane}</Box>
              <Box>{formFields}</Box>
            </Box>
          ) : (
            <>
              {mobilePreview}
              {formFields}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isDirty && <Button onClick={onClose}>Cancel</Button>}
          {primaryButton}
        </DialogActions>
      </Dialog>
    </>
  );
};
